import type { Client, Task } from '../types/crm';
import { isTempAccountCode } from './accountCode';
import { formatOutletChoiceLabel, outletLocationLabel } from './accountPurchaseHistory';

export const TEMP_ACCOUNT_CODE = 'TEMP';

export type TaskAccountDisplay = {
  name: string;
  /** Account code, or place when no separate place line is used. */
  code: string;
  /** Mother account code when useful as secondary meta. */
  motherCode?: string;
  /** Resolved outlet / area place name (e.g. Seletar). */
  outletName?: string;
  /**
   * Compact list/dashboard line:
   * company · Place  (when place set)
   * company · code   (otherwise, if code differs from name)
   * company          (fallback)
   */
  summaryLine: string;
  isProspect: boolean;
};

function buildSummaryLine(name: string, place: string, code: string): string {
  if (place) return `${name} · ${place}`;
  if (code && code !== name && code !== TEMP_ACCOUNT_CODE) return `${name} · ${code}`;
  return name;
}

/**
 * Place shown on lists / detail.
 * New tasks store the exact catalog string in outlet_name only — keep as-is.
 * Legacy NAV rows (outlet_code set) may still resolve to a district label.
 */
function resolveTaskPlace(task: Task, motherCode = ''): string {
  const outletCode = task.outlet_code?.trim() || '';
  const outletName = task.outlet_name?.trim() || '';
  const outletPostal = task.outlet_postal?.trim() || '';

  if (outletName && !outletCode) return outletName;

  return (
    outletLocationLabel(outletName, outletCode, outletPostal) ||
    outletName ||
    (outletCode
      ? formatOutletChoiceLabel(
          { code: outletCode, name: outletName || outletCode, postal: outletPostal },
          motherCode,
        )
      : '')
  );
}

/** Label for task account column / detail when linked to CRM or prospect-only. */
export function taskAccountDisplay(
  task: Task,
  client?: Client | null,
): TaskAccountDisplay {
  const motherCode = client?.code?.trim() || '';
  const locationLabel = resolveTaskPlace(task, motherCode);

  if (client) {
    const name = client.company_name;
    if (locationLabel) {
      return {
        name,
        code: motherCode,
        motherCode: motherCode || undefined,
        outletName: locationLabel,
        summaryLine: buildSummaryLine(name, locationLabel, motherCode),
        isProspect: false,
      };
    }
    return {
      name,
      code: motherCode,
      summaryLine: buildSummaryLine(name, '', motherCode),
      isProspect: false,
    };
  }

  const prospect = task.prospect_company_name?.trim();
  if (prospect) {
    return {
      name: prospect,
      code: locationLabel || TEMP_ACCOUNT_CODE,
      outletName: locationLabel || undefined,
      summaryLine: buildSummaryLine(prospect, locationLabel, TEMP_ACCOUNT_CODE),
      isProspect: true,
    };
  }

  if (locationLabel || task.outlet_code?.trim()) {
    const label = locationLabel || task.outlet_code!.trim();
    return {
      name: label,
      code: label,
      outletName: locationLabel || undefined,
      summaryLine: label,
      isProspect: false,
    };
  }

  return { name: 'No account', code: '', summaryLine: 'No account', isProspect: false };
}

export { isTempAccountCode };
